import React, { useState, useEffect } from 'react';
import { Search, Calendar, Users, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { leaveConfigAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import { useDropdownData } from '../utils/useDropdownData';
import EstablishmentCard from './EstablishmentCard';
import LeaveConfigForm from './LeaveConfigForm';
import './LeaveConfigPage.css';

/**
 * Standalone Leave Configuration Management Page
 *
 * Allows HR to:
 * - View all employees and their leave configurations
 * - Configure leave settings for individual employees
 *
 * SuperAdmin: must first select an establishment, then see employees under it.
 * ClientAdmin: establishment is auto-scoped from JWT, employee list loads immediately.
 */
const LeaveConfigPage = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [leaveFormData, setLeaveFormData] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    establishments,
    employees,
    loading: dropdownLoading,
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    clearEmployees,
  } = useDropdownData();

  // Fetch establishments for SuperAdmin on mount
  useEffect(() => {
    if (lockedEstId) return;
    fetchEstablishments();
  }, [fetchEstablishments, lockedEstId]);

  // When establishment changes, fetch employees and reset selection
  useEffect(() => {
    if (!selectedEstablishment) {
      clearEmployees();
      setFilteredEmployees([]);
      return;
    }
    setSelectedEmployee(null);
    setLeaveFormData({});
    setSearchTerm('');
    fetchEmployeesByEstablishment(selectedEstablishment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstablishment]);

  // Filter active employees based on search term
  useEffect(() => {
    const active = employees.filter(emp => emp.employment_status === 'active');
    if (searchTerm.trim() === '') {
      setFilteredEmployees(active);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredEmployees(active.filter(emp =>
        emp.employee_id.toLowerCase().includes(term) ||
        emp.first_name.toLowerCase().includes(term) ||
        emp.last_name.toLowerCase().includes(term)
      ));
    }
  }, [searchTerm, employees]);

  const handleEstablishmentChange = (e) => {
    setSelectedEstablishment(e.target.value);
  };

  const handleSelectEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setConfigLoading(true);

    try {
      const response = await leaveConfigAPI.getConfig(employee.employee_id);

      if (response.exists) {
        setLeaveFormData({
          slMonthlyAccrual: response.config.sl_monthly_accrual,
          clMonthlyAccrual: response.config.cl_monthly_accrual,
          elMonthlyAccrual: response.config.el_monthly_accrual,
          slAnnualQuota: response.config.sl_annual_quota,
          clAnnualQuota: response.config.cl_annual_quota,
          elAnnualQuota: response.config.el_annual_quota,
          isOnProbation: response.config.is_on_probation,
          probationEndDate: response.config.probation_end_date || '',
          noLeavesDuringProbation: response.config.no_leaves_during_probation,
          accrualStartDate: response.config.accrual_start_date || ''
        });
      } else {
        setLeaveFormData({
          slMonthlyAccrual: 1.0,
          clMonthlyAccrual: 1.0,
          elMonthlyAccrual: 1.25,
          slAnnualQuota: 12,
          clAnnualQuota: 12,
          elAnnualQuota: 15,
          isOnProbation: false,
          probationEndDate: '',
          noLeavesDuringProbation: false,
          accrualStartDate: ''
        });
      }
    } catch (error) {
      console.error('Error fetching leave config:', error);
      showMessage('Failed to load leave configuration', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedEmployee) return;

    setSaving(true);
    try {
      const configPayload = {
        sl_monthly_accrual: leaveFormData.slMonthlyAccrual,
        cl_monthly_accrual: leaveFormData.clMonthlyAccrual,
        el_monthly_accrual: leaveFormData.elMonthlyAccrual,
        sl_annual_quota: leaveFormData.slAnnualQuota,
        cl_annual_quota: leaveFormData.clAnnualQuota,
        el_annual_quota: leaveFormData.elAnnualQuota,
        is_on_probation: leaveFormData.isOnProbation,
        probation_end_date: leaveFormData.probationEndDate || null,
        no_leaves_during_probation: leaveFormData.noLeavesDuringProbation,
        accrual_start_date: leaveFormData.accrualStartDate || null,
        sl_balance: 0,
        cl_balance: 0,
        el_balance: 0
      };

      await leaveConfigAPI.saveConfig(selectedEmployee.employee_id, configPayload);

      showMessage(
        `Leave configuration saved for ${selectedEmployee.first_name} ${selectedEmployee.last_name}`,
        'success'
      );
      setSelectedEmployee(null);
      setLeaveFormData({});
    } catch (error) {
      console.error('Error saving leave config:', error);
      showMessage('Failed to save leave configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const empListLoading = dropdownLoading.employees;

  return (
    <div className="leave-config-page">
      <div className="leave-config-page-header">
        <div>
          <h1 className="page-title">
            <Calendar size={28} />
            Leave Configuration Management
          </h1>
          <p className="page-subtitle">
            Configure monthly accrual rates and annual quotas for employees
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Establishment selector — visible for SuperAdmin only, hidden for ClientAdmin */}
      <EstablishmentCard
        establishments={establishments}
        value={selectedEstablishment}
        onChange={handleEstablishmentChange}
        loading={dropdownLoading.establishments}
        style={{ marginBottom: 24 }}
      />

      <div className="leave-config-layout">
        {/* Employee List Sidebar */}
        <div className="employee-sidebar">
          <div className="sidebar-header">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!selectedEstablishment}
              />
            </div>
            <div className="employee-count">
              <Users size={16} />
              {filteredEmployees.length} {filteredEmployees.length === 1 ? 'Employee' : 'Employees'}
            </div>
          </div>

          {!selectedEstablishment ? (
            <div className="sidebar-loading">
              <Users size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>Select an organization above</p>
            </div>
          ) : empListLoading ? (
            <div className="sidebar-loading">
              <Loader2 className="spin" size={24} />
              <p>Loading employees...</p>
            </div>
          ) : (
            <div className="employee-list">
              {filteredEmployees.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} />
                  <p>No employees found</p>
                </div>
              ) : (
                filteredEmployees.map(employee => (
                  <div
                    key={employee.employee_id}
                    className={`employee-card ${selectedEmployee?.employee_id === employee.employee_id ? 'selected' : ''}`}
                    onClick={() => handleSelectEmployee(employee)}
                  >
                    <div className="employee-info">
                      <span className="employee-name">
                        {employee.first_name} {employee.last_name}
                      </span>
                      <span className="employee-id">{employee.employee_id}</span>
                    </div>
                    {employee.department && (
                      <span className="employee-department">{employee.department}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Configuration Panel */}
        <div className="config-panel">
          {!selectedEmployee ? (
            <div className="no-selection">
              <Calendar size={64} />
              <h3>Select an Employee</h3>
              <p>Choose an employee from the list to configure their leave settings</p>
            </div>
          ) : configLoading ? (
            <div className="panel-loading">
              <Loader2 className="spin" size={40} />
              <p>Loading configuration...</p>
            </div>
          ) : (
            <>
              <div className="selected-employee-banner">
                <div>
                  <h2>{selectedEmployee.first_name} {selectedEmployee.last_name}</h2>
                  <p>Employee ID: {selectedEmployee.employee_id}</p>
                </div>
              </div>

              <LeaveConfigForm
                formData={leaveFormData}
                setFormData={setLeaveFormData}
                showTitle={false}
              />

              <div className="config-actions">
                <button
                  className="btn-save"
                  onClick={() => setShowConfirm(true)}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Configuration
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="lc-confirm-backdrop" onClick={() => setShowConfirm(false)}>
          <div className="lc-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="lc-confirm-icon">
              <Save size={28} />
            </div>
            <h3>Save Leave Configuration?</h3>
            <p>
              You are about to save leave settings for{' '}
              <strong>{selectedEmployee?.first_name} {selectedEmployee?.last_name}</strong>{' '}
              ({selectedEmployee?.employee_id}). This will update their accrual rates and annual quotas.
            </p>
            <div className="lc-confirm-actions">
              <button
                className="lc-btn-cancel"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="lc-btn-confirm"
                onClick={() => { setShowConfirm(false); handleSaveConfig(); }}
              >
                <Save size={16} />
                Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveConfigPage;
